import { useCallback, useEffect } from 'react';
import { AuthErrorType, handleError } from '../api/base';
import { BASE_API_URL } from '../config';

export interface ProductSpawnData {
  model_id: string;
  name: string;
  height: string;
  width: string;
  depth: string;
  mouseX: number;
  mouseY: number;
}

interface UnityBridgeCallbacksProps {
  onExportImage?: (
    imageBytes: Uint8Array,
    fileName: string,
    extension: string
  ) => void;
  onExportCSV?: (
    csvString: string,
    fileName: string,
    extension: string
  ) => void;
  onExportSegment?: (segmentFile: Uint8Array, segmentName: string) => void;
  onExportZip?: (
    zipFile: Uint8Array,
    fileName: string,
    extension: string
  ) => void;
  onExportPDF?: (pdfJSON: string, fileName: string, extension: string) => void;
  onExportPSA?: (
    psaBytes: Uint8Array | number[],
    fileName: string,
    extension: string
  ) => void;
  onAuthReady?: () => void;
  onProjectReady?: () => void;
  onSetDirty?: (isDirty: boolean) => void;
  onReturnToProjects?: () => void;
}

interface UnityBridgeProps {
  projectId: string;
  callbacks: UnityBridgeCallbacksProps;
}

export const useUnityBridge = ({ projectId, callbacks }: UnityBridgeProps) => {
  useEffect(() => {
    const handleUnityMessage = (event: MessageEvent) => {
      switch (event.data.type) {
        case 'EXPORT_IMAGE':
          callbacks.onExportImage?.(
            event.data.imageData.imageBytes,
            event.data.imageData.fileName,
            event.data.imageData.extension
          );
          break;
        case 'EXPORT_CSV':
          callbacks.onExportCSV?.(
            event.data.csvData.csvString,
            event.data.csvData.fileName,
            event.data.csvData.extension
          );
          break;
        case 'EXPORT_ZIP':
          callbacks.onExportZip?.(
            event.data.zipData.zipFile,
            event.data.zipData.fileName,
            event.data.zipData.extension
          );
          break;
        case 'EXPORT_PDF':
          console.log('React received PDF export request from Unity');
          console.log('PDF data received:', {
            fileName: event.data.pdfData.fileName,
            extension: event.data.pdfData.extension,
            pdfJSONLength: event.data.pdfData.pdfJSON.length,
            pdfJSONPreview: event.data.pdfData.pdfJSON.substring(0, 500),
          });
          callbacks.onExportPDF?.(
            event.data.pdfData.pdfJSON,
            event.data.pdfData.fileName,
            event.data.pdfData.extension
          );
          break;
        case 'EXPORT_PSA':
          callbacks.onExportPSA?.(
            event.data.psaData.psaBytes,
            event.data.psaData.fileName,
            event.data.psaData.extension
          );
          break;
        case 'AUTH_READY':
          callbacks.onAuthReady?.();
          break;
        case 'PROJECT_READY':
          callbacks.onProjectReady?.();
          break;
        case 'SET_DIRTY':
          callbacks.onSetDirty?.(Boolean(event.data.isDirty));
          break;
        case 'RETURN_TO_PROJECTS':
          callbacks.onReturnToProjects?.();
          break;
      }
    };

    window.addEventListener('message', handleUnityMessage);
    return () => window.removeEventListener('message', handleUnityMessage);
  }, [callbacks]);

  const exportImage = useCallback(
    async (
      imageBytes: number[] | Uint8Array,
      fileName: string,
      extension = 'png'
    ) => {
      try {
        console.log('Raw image data:', imageBytes);
        console.log('Image data type:', typeof imageBytes);

        const bytes =
          imageBytes instanceof Uint8Array
            ? imageBytes
            : new Uint8Array(imageBytes);

        const blob = new Blob([bytes as any], { type: `image/${extension}` });

        try {
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: `${fileName}.${extension}`,
            types: [
              {
                description: 'Image File',
                accept: {
                  'image/*': [`.${extension}`],
                },
              },
            ],
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
        } catch (pickerError) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${fileName}.${extension}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }

        console.log('Image exported successfully:', fileName);
      } catch (error) {
        if ((error as any).name !== 'AbortError') {
          console.error('Error exporting image:', error);
        }
      }
    },
    []
  );

  const exportCSV = useCallback(
    async (csvString: string, fileName: string, extension = 'csv') => {
      try {
        const blob = new Blob([csvString], { type: 'text/csv' });

        const handle = await (window as any).showSaveFilePicker({
          suggestedName: `${fileName}.${extension}`,
          types: [
            {
              description: 'CSV File',
              accept: {
                'text/csv': [`.${extension}`],
              },
            },
          ],
        });

        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();

        console.log('CSV exported successfully:', fileName);
      } catch (error) {
        if ((error as any).name !== 'AbortError') {
          console.error('Error exporting CSV:', error);
        }
      }
    },
    []
  );

  const exportZip = useCallback(
    async (zipFile: Uint8Array, fileName: string, extension = 'zip') => {
      try {
        const blob = new Blob([zipFile as any], { type: 'application/zip' });

        const handle = await (window as any).showSaveFilePicker({
          suggestedName: `${fileName}.${extension}`,
          types: [
            {
              description: 'ZIP Archive',
              accept: {
                'application/zip': [`.${extension}`],
              },
            },
          ],
        });

        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();

        console.log('ZIP exported successfully:', fileName);
      } catch (error) {
        if ((error as any).name !== 'AbortError') {
          console.error('Error exporting ZIP:', error);
        }
      }
    },
    []
  );

  const sendPDFProcessingComplete = useCallback((status: string) => {
    console.log('1. PDF Processing Complete');
    console.log('Status:', status);
    const unityFrame = document.querySelector('iframe');

    if (unityFrame?.contentWindow) {
      console.log('2. PDF Processing Complete via iframe');
      console.log('Status:', status);
      unityFrame.contentWindow.postMessage(
        {
          type: 'PDF_PROCESSING_COMPLETE',
          status: status,
        },
        '*'
      );
    } else if ((window as any).unityInstance) {
      console.log('3. PDF Processing Complete via SendMessage');
      console.log('Status:', status);
      (window as any).unityInstance.SendMessage(
        'PDFSection',
        'PDFProcessingComplete',
        status
      );
    }
  }, []);

  const exportPDF = useCallback(
    async (
      pdfJSON: string,
      fileName: string,
      extension = 'pdf',
      images?: any[]
    ) => {
      try {
        console.log('Processing PDF export:', { fileName, extension });
        console.log('PDF JSON data type:', typeof pdfJSON);
        console.log('PDF JSON data length:', pdfJSON.length);
        console.log(
          'PDF JSON preview (first 500 chars):',
          pdfJSON.substring(0, 500)
        );

        const requestBody = {
          csvData: pdfJSON,
          ...(images && images.length > 0 && { images }),
        };

        console.log('Sending PDF data to API...');
        const response = await fetch(
          `${BASE_API_URL()}/planogram/${projectId}/createPdf`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${window.localStorage.getItem('token')}`,
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (response.status === 401) {
          const authError = {
            error: {
              code: 401,
              type: AuthErrorType.UNAUTHORIZED,
              message: 'Unauthorized request',
            },
          };
          sendPDFProcessingComplete('error');
          handleError(authError);
          return;
        }

        if (!response.ok) {
          const errorMessage = `API responded with status: ${response.status}`;
          console.error('PDF export error:', errorMessage);
          throw new Error(errorMessage);
        }

        const pdfBlob = await response.blob();
        console.log('PDF blob received:', pdfBlob);

        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        console.log('PDF downloaded successfully:', fileName);

        sendPDFProcessingComplete('success');
      } catch (error) {
        if ((error as any).name !== 'AbortError') {
          console.error('Error exporting PDF:', error);
          const apiError = {
            error: {
              code: 500,
              type: AuthErrorType.OTHER,
              message: (error as Error).message || 'PDF export failed',
            },
          };
          handleError(apiError);

          sendPDFProcessingComplete('error');
        } else {
          console.log('PDF export cancelled by user');
          sendPDFProcessingComplete('cancelled');
        }
      }
    },
    [projectId, sendPDFProcessingComplete]
  );

  const exportPSA = useCallback(
    async (
      psaBytes: Uint8Array | number[],
      fileName: string,
      extension = 'psa'
    ) => {
      try {
        console.log('Processing PSA export:', { fileName, extension });

        const bytes = Array.isArray(psaBytes)
          ? new Uint8Array(psaBytes)
          : psaBytes;

        const blob = new Blob([bytes as any], {
          type: 'text/plain;charset=utf-8',
        });

        try {
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: `${fileName}.${extension}`,
            types: [
              {
                description: 'PSA File',
                accept: {
                  'text/plain': [`.${extension}`],
                },
              },
            ],
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();

          console.log('PSA file exported successfully:', fileName);
        } catch (pickerError) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${fileName}.${extension}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          console.log('PSA file exported via download link:', fileName);
        }
      } catch (error) {
        if ((error as any).name !== 'AbortError') {
          console.error('Error exporting PSA:', error);
        }
      }
    },
    []
  );

  const sendAuthToken = useCallback(() => {
    const token = localStorage.getItem('token');

    if (token) {
      const unityFrame = document.querySelector('iframe');

      if (unityFrame?.contentWindow) {
        unityFrame.contentWindow.postMessage(
          {
            type: 'RECEIVE_AUTH_TOKEN',
            token: token,
          },
          '*'
        );
      } else if ((window as any).unityInstance) {
        (window as any).unityInstance.SendMessage(
          'WebPortalBridge',
          'ReceiveAuthToken',
          token
        );
      }

      return true;
    }

    return false;
  }, []);

  const loadProject = useCallback((projectId: string, libraryPath?: string) => {
    const unityFrame = document.querySelector('iframe');
    if (unityFrame?.contentWindow) {
      try {
        (unityFrame.contentWindow as any).LoadProject(projectId, libraryPath);
      } catch (error) {
        console.warn(
          'LoadProject function not available directly, using SendMessage fallback'
        );
        if ((window as any).unityInstance) {
          (window as any).unityInstance.SendMessage(
            'WebPortalBridge',
            'LoadProject',
            projectId,
            libraryPath || ''
          );
        }
      }
    } else if ((window as any).unityInstance) {
      (window as any).unityInstance.SendMessage(
        'WebPortalBridge',
        'LoadProject',
        projectId,
        libraryPath || ''
      );
    }
  }, []);

  return {
    exportImage,
    exportCSV,
    exportZip,
    exportPDF,
    exportPSA,
    sendAuthToken,
    sendPDFProcessingComplete,
    loadProject,
  };
};
